import { NAV_DASHBOARD_MAPPING } from '../../layouts/navPermissions';

/**
 * The dbo.DashboardData Oid a To Do List → Sample Backlog / ICM nav entry shows, or null when
 * the entry is not a legacy dashboard (e.g. Pending Jobs, an AI-only page).
 *
 * ONE source of truth with the menu's own gate: the same NAV_DASHBOARD_MAPPING decides whether
 * the entry is visible (the user's allowedDashboards) and which dashboard it opens, so a user
 * can never see an entry that opens a dashboard they are not assigned (ADR-0016).
 */
export function backlogDashboardId(navId: string): string | null {
  return NAV_DASHBOARD_MAPPING[navId]?.[0] ?? null;
}

/**
 * Sample Backlog / ICM nav ids that render as the OLD native grid DRIVEN BY the
 * dashboard XML (features/backlog/BacklogXmlGridPage) instead of the DevExpress
 * dashboard viewer. Columns, filters and the data stored-proc all come from that
 * entry's dbo.DashboardData XML (parsed by the API) — nothing hardcoded — so the
 * native grid matches exactly what legacy ALPACA shows for the same dashboard.
 *
 * Being migrated ONE ENTRY AT A TIME (client request 2026-10-05: eventually every
 * backlog entry uses the native grid). Any id NOT listed here still shows the
 * DevExpress viewer, so adding a backlog is a single line here (no other change —
 * BacklogXmlGridPage is generic over the navId's mapped dashboard Oid).
 */
export const XML_GRID_BACKLOG = new Set<string>([
  'bl-report',   // Backlog 08 - Reporting      (first, 2026-10-05)
  'bl-coc',      // 00_Missing_COC              (2026-10-05)
  // Remaining single-grid, stored-proc-backed dashboards — verified 2026-10-05 that
  // each parses to ONE SP grid whose SP runs param-less and returns rows.
  'bl-track',    // Backlog 01 - Sample Tracking       (Dashboard_SelectPendigSubmission_SP)
  'bl-transfer', // Backlog 02 - Pending Transfer      (Dashboard_PendingTransfer_Sp)
  'bl-prep',     // Backlog 03 - Sample Preparation    (Dashboard_Samplepreparation_SP)
  'bl-sdms',     // Backlog 04 - SDMS Result Entry     (Dashboard_PendingResultEntry_SP)
  'bl-review',   // Backlog 06 - Field Data Validation (Dashboard_PendingValidation_SP)
  'bl-rappr',    // Backlog 07 - Result Approval       (Dashboard_PendingResultApproval_SP)
  'icm01',       // ICM01 - Pending Orders             (Dashboard_Pendingorders_SP)
  'icm02',       // ICM02 - Pending Validation         (Dashboard_PendingValidation_View)
  'icm03',       // ICM03 - Pending Receipt            (Dashboard_OrderPlacedNotRecived_SP)
  'icm05',       // ICM05 - Pending COA                (Dashboard_PendingReagentCertificates_COAs_SP)
  'bl-pjobs',    // Pending Jobs                       (AI_Backlog_PendingJobs_SP)
  // Backlog 00 - Due Samples: a COMPOSITE (3 KPI panels Due Today/Tomorrow/Day-After
  // over DueToday/DueTomorrow/DayAfterTomorrow SPs + the Sample Details grid over
  // Task_Due). Converted 2026-10-05 (owner: "native grid + KPI panels") — the API
  // parses the dashboard's <Groups> of single-measure mini-grids into KPI panels and
  // SUMs each; the native page renders the panels above the Sample Details grid.
  'bl-due',      // Backlog 00 - Due Samples
  // Backlog 02 - Subout Tracking: a 2-GRID composite (Subout Result Entry over
  // Dashboard_Subout_Result_Entry + Pending Subout Registration over
  // Dashboard_PendingSuboutRegistration_SP). Converted 2026-10-05 (owner: "convert
  // subout track to native") — the API returns BOTH grids (DashboardGridResult.Grids,
  // in LayoutTree order) and the native page stacks them as two cards.
  'bl-subout',   // Backlog 02 - Subout Tracking
  // Every Sample Backlog / ICM dashboard is now native.
]);

/** True when this nav id should render the XML-driven native grid (see XML_GRID_BACKLOG). */
export function isXmlGridBacklog(navId: string): boolean {
  return XML_GRID_BACKLOG.has(navId);
}
